// NotificationsPanel — the in-app merchant notifications sub-section (TD-384
// T26; T25's read surface, design §4.4.7). Deliberately NOT under "Refer & Save":
// the notification surface is FIRST-CLASS shared infrastructure (memory
// feedback_first_class_shared_infra); the next feature that needs to reach
// a merchant extends `kind`, and it must not grow an umbrella-tab
// dependency on the referral program's naming. See AMENDED below for how
// and where it is mounted today.
//
// ⚠️ AMENDED 2026-09-11 (Unit 1 — My Account shell + IA)
//   ORIGINAL: "Mounted as a top-level My Account tab by AccountCenter —
//   deliberately NOT under 'Refer & Save': the notification surface is
//   FIRST-CLASS shared infrastructure ... and it must not grow an
//   umbrella-tab dependency on the referral program's naming."
//
//   CURRENT (through the account-area redesign, Task 4): mounted by
//   AccountCenter as its own top-level Notifications SECTION. The ORIGINAL's
//   reasoning is PRESERVED, not overridden: its concern is coupling this
//   surface to a FEATURE tab (the referral program's naming). The account
//   landing is not a feature, and this panel remains structurally
//   first-class — its own component, its own notification-api module, and
//   its own ACL_AREA_READ_NOTIFICATIONS gate. The mount is additionally
//   gated on AccountCenter's `selfViewSettled` flag, so this panel does not
//   mount — and therefore dispatches no fetch — until the ACL self-view
//   answer has arrived. Design:
//   _src/docs/plans/2026-09-11__unit1-my-account-shell-ia__design.md §3.
//
// ⚠️ AMENDED 2026-10-03 (account-area redesign Task 6, the F2/O2 amendment)
//   ORIGINAL: two cards (a heading card + a list card), the heading card
//   carrying this panel's own "Notifications" h2 and a referral-program
//   intro sentence; rows were card-per-row.
//
//   CURRENT: ONE card of role="list"/"listitem" rows
//   (NotificationListItem, EXPORTED — the Overview landing's Notifications
//   block renders the same rows, so a kind label, the unread treatment, or
//   a payload fact can never drift between the two surfaces). The internal
//   heading and the intro sentence are GONE: the account shell paints the
//   section's heading, and the registry's description carries the
//   orientation copy — same divergence OrdersTab reported in Task 5. An
//   unread row carries a visible dot PLUS the visually-hidden word "Unread",
//   so the read state never rests on colour alone (WCAG 1.4.1); the dot is
//   aria-hidden because the sr-only text is the accessible signal. Mark
//   read is a quiet EmbedLink now — a row action, not a primary control.
//
// The badge is the SERVER's unread count read off the list envelope —
// never re-derived from the rows (the two are exactly what must not be
// allowed to drift apart silently). Row order is the API's (unread-first,
// newest-first within each read state; array position IS recency — no
// timestamp renders, rule #9).
//
// §30 in the payload render: a settled notice carries BOTH amounts in its
// envelope; when they differ BOTH are shown — the merchant sees the
// adjustment, never just the final figure. Unknown/future kinds render
// their kind verbatim with no payload guesswork.
//
// Inline styles only — no Tailwind (embed convention). Every <button> is
// explicitly type="button" (project rule §73; EmbedLink/EmbedButton do it
// at birth).

import { useState, useEffect, useCallback, type CSSProperties, type ReactNode } from "react";
import {
  useEmbedTokens,
  EmbedCard,
  EmbedText,
  EmbedButton,
  EmbedBanner,
  EmbedLink,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { formatCurrency } from "../../embed-shared/currency";
import { listMyNotifications, markMyNotificationRead } from "./notification-api";
import type { EmbedCustomerNotification } from "./notification-api";

// Known kinds get a friendly label; anything else renders verbatim — the
// closed map is a display nicety, never a gate (first-class surface).
const KIND_LABELS: Record<string, string> = {
  referral_redemption_settled: "Redemption settled",
  referral_redemption_revoked: "Redemption revoked",
  referral_redemption_adjusted: "Redemption adjusted",
};

function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

/** The merchant-useful subset of a kind's payload envelope, rendered only
 * when present — future kinds' unknown keys are skipped, not guessed. */
function PayloadFacts({ payload }: { payload: Record<string, string> }) {
  const facts: string[] = [];
  if (payload.mid_number) facts.push(`MID ${payload.mid_number}`);
  if (payload.statement_month) facts.push(`${payload.statement_month} statement`);
  const money: string[] = [];
  if (payload.requested_amount) money.push(`Requested ${formatCurrency(Number(payload.requested_amount))}`);
  if (payload.verified_amount) money.push(`Verified ${formatCurrency(Number(payload.verified_amount))}`);
  if (payload.remaining_balance)
    money.push(`Remaining balance ${formatCurrency(Number(payload.remaining_balance))}`);
  if (money.length > 0) facts.push(money.join(" · "));
  if (facts.length === 0) return null;
  return (
    <EmbedText tone="secondary" size="xs">
      {facts.join(" · ")}
    </EmbedText>
  );
}

/** The visually-hidden text pattern (jsdom-safe inline form) — carries the
 *  "Unread" state to assistive tech while the dot carries it visually. */
const SR_ONLY: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
};

/**
 * ONE notification row, shared by this panel and the Overview landing's
 * Notifications block (account-area redesign Task 6). `action` (the panel's
 * quiet "Mark read") renders at the row's trailing edge; the Overview block
 * omits it — read state is managed on the full list, not from the summary.
 * `last` retires the row divider on the final row.
 */
export function NotificationListItem({
  n,
  action,
  last = false,
}: {
  n: EmbedCustomerNotification;
  action?: ReactNode;
  last?: boolean;
}) {
  const t = useEmbedTokens();
  const isUnread = n.read_at === undefined;
  return (
    <li
      role="listitem"
      data-testid={`notification-${n.id}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: t.spacing.xs,
        padding: `${t.spacing.sm}px 0`,
        borderBottom: last ? "none" : `1px solid ${t.colors.border}`,
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: t.spacing.sm, minWidth: 0 }}>
        {isUnread && (
          <>
            <span
              data-unread-dot
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: t.colors.primary,
                flex: "0 0 auto",
              }}
            />
            <span style={SR_ONLY}>Unread</span>
          </>
        )}
        <span
          style={{
            fontSize: t.fontSize.sm,
            fontWeight: isUnread ? 600 : 500,
            color: t.colors.text,
            minWidth: 0,
          }}
        >
          {kindLabel(n.kind)}
        </span>
        {action && <span style={{ marginLeft: "auto", flex: "0 0 auto" }}>{action}</span>}
      </div>
      <PayloadFacts payload={n.payload} />
    </li>
  );
}

export function NotificationsPanel() {
  const t = useEmbedTokens();

  const [rows, setRows] = useState<EmbedCustomerNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);

  const messageOf = useCallback((reason: unknown, fallback: string): string => {
    return reason instanceof ApiError ? reason.message : fallback;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listMyNotifications();
      setRows(res.data);
      setUnread(res.unread);
    } catch (err) {
      setError(messageOf(err, "Failed to load your notifications"));
    } finally {
      setLoading(false);
    }
  }, [messageOf]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkRead = useCallback(
    async (row: EmbedCustomerNotification) => {
      setMarkingId(row.id);
      setError(null);
      try {
        const updated = await markMyNotificationRead(row.id, row.version);
        // §59: re-seed the row from the MUTATION RESPONSE — its read state
        // and version are the server's truth; the badge follows the row's
        // read_at transition, not a blind decrement.
        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setUnread((prev) => (row.read_at === undefined ? Math.max(0, prev - 1) : prev));
      } catch (err) {
        setError(messageOf(err, "Failed to mark this notification read."));
      } finally {
        setMarkingId(null);
      }
    },
    [messageOf],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      <EmbedCard>
        {error && (
          <div style={{ marginBottom: t.spacing.md }}>
            <EmbedBanner tone="error">{error}</EmbedBanner>
            <div style={{ marginTop: t.spacing.md }}>
              <EmbedButton type="button" variant="secondary" onClick={load}>
                Retry
              </EmbedButton>
            </div>
          </div>
        )}
        {loading && (
          <p role="status" style={{ margin: 0 }}>
            <EmbedText tone="secondary">Loading your notifications...</EmbedText>
          </p>
        )}
        {!loading && !error && rows.length === 0 && (
          <p role="status" style={{ margin: 0 }}>
            <EmbedText tone="secondary">No notifications yet.</EmbedText>
          </p>
        )}
        {!loading && rows.length > 0 && (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: t.spacing.xs }}>
              <span
                style={{
                  fontSize: t.fontSize.xs,
                  fontWeight: 600,
                  color: unread > 0 ? t.colors.primary : t.colors.textSecondary,
                }}
                aria-live="polite"
              >
                {unread === 0 ? "All read" : `${unread} unread`}
              </span>
            </div>
            <ul
              role="list"
              style={{
                margin: 0,
                padding: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {rows.map((n, i) => (
                <NotificationListItem
                  key={n.id}
                  n={n}
                  last={i === rows.length - 1}
                  action={
                    n.read_at === undefined ? (
                      <EmbedLink
                        onClick={() => {
                          if (markingId === n.id) return;
                          void handleMarkRead(n);
                        }}
                      >
                        {markingId === n.id ? "Marking..." : "Mark read"}
                      </EmbedLink>
                    ) : undefined
                  }
                />
              ))}
            </ul>
          </>
        )}
      </EmbedCard>
    </div>
  );
}
